import { useEffect, useState, useCallback, useMemo } from "react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Search,
  HelpCircle,
  User,
  LogOut,
  Keyboard,
  Newspaper,
  Plug,
  ExternalLink,
  Map,
  Settings,
} from "lucide-react"
import { useAuth } from "@/providers/auth-provider"
import { useTheme } from "@/hooks/use-theme"
import { fetchAccountProfile } from "@/lib/api"
import { useDetectedShortcutOs } from "@/hooks/use-shortcut-os"
import { formatModShortcut } from "@/lib/keyboard-platform"
import { getPublicChangelogUrl, getPublicHelpUrl } from "@/lib/public-site"
import { openSettings } from "@/lib/open-settings"
import { testIds } from "@/lib/test-ids"
import {
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_CLASS,
  QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS,
} from "@/lib/menu-chrome"
import { SHELL_APP_HEADER_CLASS } from "@/lib/shell-chrome"

interface HeaderProps {
  onSearchClick?: () => void
  onKeyboardShortcutsClick?: () => void
  onTakeTourClick?: () => void
  className?: string
}

export function Header({ 
  onSearchClick, 
  onKeyboardShortcutsClick,
  onTakeTourClick,
  className = ""
}: HeaderProps) {
  const { user, isAuthConfigured, signOut } = useAuth()
  const { resolvedTheme } = useTheme()
  const [profileFullName, setProfileFullName] = useState<string | null>(null)
  const detectedOs = useDetectedShortcutOs()

  const searchShortcut = useMemo(
    () => formatModShortcut(detectedOs, 'f', { shift: true }),
    [detectedOs]
  )

  const wordmarkSrc =
    resolvedTheme === 'dark' ? '/maater-wordmark-dark.png' : '/maater-wordmark.png'

  const loadProfileFullName = useCallback(async () => {
    if (!isAuthConfigured || !user) {
      setProfileFullName(null)
      return
    }
    try {
      const data = await fetchAccountProfile()
      setProfileFullName(data.fullName?.trim() || null)
    } catch {
      setProfileFullName(null)
    }
  }, [isAuthConfigured, user])

  useEffect(() => {
    void loadProfileFullName()
  }, [loadProfileFullName])

  useEffect(() => {
    const onProfileUpdated = () => {
      void loadProfileFullName()
    }
    window.addEventListener('flownotes:account-profile-updated', onProfileUpdated)
    return () => window.removeEventListener('flownotes:account-profile-updated', onProfileUpdated)
  }, [loadProfileFullName])

  const metadataFullName =
    typeof user?.user_metadata?.full_name === 'string'
      ? user.user_metadata.full_name.trim()
      : ''
  const displayLabel =
    isAuthConfigured && user
      ? profileFullName || metadataFullName || user.email || 'Account'
      : (import.meta.env.VITE_LOCAL_USER_LABEL?.trim() || 'Local (no Supabase login)')

  return (
    <header className={`${SHELL_APP_HEADER_CLASS} border-b border-border bg-background flex items-center justify-between px-4 flex-shrink-0 mt-0 ${className}`} data-testid={testIds.header.root}>
      {/* Left: Branding + Sidebar Controls */}
      <div className="flex items-center gap-4">
        <a
          href="/"
          className="inline-flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
          data-testid={testIds.header.wordmark}
          aria-label="Maater"
        >
          <img
            src={wordmarkSrc}
            alt="Maater"
            width={140}
            height={28}
            className="h-5 w-auto select-none"
            draggable={false}
          />
        </a>
      </div>

      {/* Center: Global Search */}
      <div className="flex-1 max-w-md mx-4">
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('flownotes:open-search-view', { detail: { query: '' } }))}
          data-testid={testIds.header.search}
          className="relative w-full h-9 flex items-center gap-3 px-3 rounded-md border border-border bg-muted/50 hover:bg-muted transition-colors text-left group"
        >
          <Search className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Search... ({searchShortcut})</span>
          <kbd className="ml-auto inline-flex h-5 select-none items-center gap-1 rounded border border-border bg-background px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100 group-hover:border-primary/50 transition-colors">
            {searchShortcut}
          </kbd>
        </button>
      </div>

      {/* Right: account chip and Help. Panel toggles live on the columns. */}
      <div className="flex items-center gap-2">
        {isAuthConfigured ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="gap-2"
                data-testid={testIds.header.userMenu}
              >
                <User className="h-4 w-4" />
                <span className="text-sm truncate max-w-[160px]">{displayLabel}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className={QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS}>
              <DropdownMenuItem
                onClick={() => openSettings({ tab: 'account' })}
                className="gap-2"
                data-testid={testIds.header.accountButton}
              >
                <Settings className="h-4 w-4" />
                Account
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => signOut()} className="gap-2">
                <LogOut className="h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="gap-2"
            data-testid={testIds.header.userMenu}
          >
            <User className="h-4 w-4" />
            <span className="text-sm">{displayLabel}</span>
          </Button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              title="Help"
              data-testid={testIds.header.helpMenu}
            >
              <HelpCircle className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
            collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
            data-testid={testIds.header.helpMenuContent}
          >
            {onTakeTourClick ? (
              <DropdownMenuItem
                onClick={onTakeTourClick}
                className="gap-2"
                data-testid={testIds.header.takeTourButton}
              >
                <Map className="h-4 w-4" />
                Take a tour
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem
              onClick={onKeyboardShortcutsClick}
              className="gap-2"
              data-testid={testIds.header.keyboardShortcutsButton}
            >
              <Keyboard className="h-4 w-4" />
              Keyboard shortcuts
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <a
                href={getPublicHelpUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="gap-2 cursor-pointer"
                data-testid={testIds.header.helpGuidesLink}
              >
                <HelpCircle className="h-4 w-4" />
                Getting started
                <ExternalLink className="h-3 w-3 ml-auto text-muted-foreground" />
              </a>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <a
                href={getPublicHelpUrl('mcp')}
                target="_blank"
                rel="noopener noreferrer"
                className="gap-2 cursor-pointer"
                data-testid={testIds.header.helpMcpLink}
              >
                <Plug className="h-4 w-4" />
                Connect MCP
                <ExternalLink className="h-3 w-3 ml-auto text-muted-foreground" />
              </a>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <a
                href={getPublicChangelogUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="gap-2 cursor-pointer"
                data-testid={testIds.header.changelogLink}
              >
                <Newspaper className="h-4 w-4" />
                What&apos;s new
                <ExternalLink className="h-3 w-3 ml-auto text-muted-foreground" />
              </a>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
